// Снимки состояния: сохранить текущее и вернуться к сохранённому.
// Живут в верхней полосе справа — рядом с логотипом и основными инструментами.
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { AppState } from '../types';
import { useT } from '../i18n';
import { useAuth } from '../auth';
import { useTrips } from '../trips';
import type { SyncState } from './useServerSync';
import { createSnapshot, fetchSnapshot, listSnapshots, type SnapshotMeta } from './api';
import styles from './SnapshotControls.module.css';

export interface SnapshotControlsProps {
  sync: SyncState;
  onRestore(state: AppState): void;
}

/** Дискета — привычный знак «сохранить»; журнал — стопка строк со списком. */
function SaveIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h10L20 8.5v10a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5z" />
      <path d="M8 4v5h7V4.5" />
      <rect x="8" y="13" width="8" height="7" rx="1" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6.5A1.5 1.5 0 0 1 5 19.5z" />
      <path d="M5 17.5h13" />
      <path d="M9 7.5h6M9 11h6" />
    </svg>
  );
}

/** Замок поверх иконки — гостю: то же действие есть, но за входом. */
function LockIcon() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function formatStamp(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function SnapshotControls({ sync, onRestore }: SnapshotControlsProps): ReactElement | null {
  const t = useT();
  const { isGuest, status, requestSignIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<SnapshotMeta[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // История своя у каждой поездки: список снимков соседней здесь был бы просто
  // набором чужих состояний, которые нечем отличить друг от друга.
  const { currentId } = useTrips();

  const refresh = useCallback(async () => {
    setSnapshots(await listSnapshots(currentId));
  }, [currentId]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  // Кнопки стоят в полосе всегда — и гостю, и вошедшему: прячущаяся кнопка
  // выглядит как баг, а не как состояние. Гостю она ведёт к входу, вошедшему
  // при недоступном сервере — честно пытается и показывает ошибку в title и
  // маркером на кнопке (ниже), а не исчезает. Пока не ясно, вошёл ли человек,
  // на месте держим пустую заглушку той же ширины — так же, как AccountButton.
  if (status === 'loading') {
    return <span className={styles.placeholder} aria-hidden="true" />;
  }

  // Маркер ошибки — только у настоящей проблемы синхронизации вошедшего:
  // гостю нечего ни отклонять, ни конфликтовать, у него sync.status === 'local'.
  const hasIssue = !isGuest && (sync.status === 'rejected' || sync.status === 'conflict');
  const issueTone = sync.status === 'rejected' ? styles.error : styles.warn;

  // Короткая метка статуса уходит в title кнопки-дискеты (была видна в полосе
  // отдельным текстом, теперь — во всплывающей подсказке и для скринридера).
  const statusLabel = t(`sync.${sync.status}`);
  const statusDetail = t(`sync.${sync.status}.tip`);
  const revSuffix = sync.rev > 0 ? ` · r${sync.rev}` : '';
  const errorSuffix = sync.errors.length > 0 ? ` — ${sync.errors.join('; ')}` : '';

  const saveTitle = isGuest
    ? t('auth.guest.locked')
    : `${t('sync.snapshot.tip')} — ${statusLabel}${revSuffix}${errorSuffix}`;
  const historyTitle = isGuest ? t('auth.guest.locked') : t('sync.history.tip');
  const statusText = isGuest ? t('auth.guest.locked') : `${statusDetail}${errorSuffix}`;

  const onSnapshot = async () => {
    setBusy(true);
    const ok = await createSnapshot(
      t('sync.snapshot.label', { stamp: formatStamp(Date.now()) }),
      undefined,
      currentId,
    );
    setBusy(false);
    setMessage(t(ok ? 'sync.snapshot.saved' : 'sync.snapshot.failed'));
    if (ok && open) void refresh();
  };

  const onPick = async (id: number) => {
    const state = await fetchSnapshot(id);
    if (!state) {
      setMessage(t('sync.snapshot.notFound'));
      return;
    }
    onRestore(state);
    setOpen(false);
    setMessage(t('sync.snapshot.restored'));
  };

  return (
    <div className={styles.wrap} ref={boxRef}>
      {/* Подсказка висит на обёртке, а не на самой кнопке: так работает
          одинаково и с aria-disabled, и без него, и после смены title при
          каждой смене статуса синхронизации. */}
      <span title={saveTitle}>
        <button
          type="button"
          className={`btn btn-sm ${styles.iconBtn} ${isGuest ? styles.locked : ''}`}
          onClick={isGuest ? requestSignIn : onSnapshot}
          disabled={busy}
          aria-label={t('sync.snapshot')}
        >
          <SaveIcon />
          {isGuest && (
            <span className={styles.lockBadge} aria-hidden="true">
              <LockIcon />
            </span>
          )}
          {/* Точка-маркер — тот же приём, что у неподтверждённой почты в
              AccountButton: конфликт и отказ сервера иначе были бы видны
              только через наведение на кнопку. */}
          {hasIssue && <span className={`${styles.statusDot} ${issueTone}`} aria-hidden="true" />}
        </button>
      </span>

      <span title={historyTitle}>
        <button
          type="button"
          className={`btn btn-sm ${styles.iconBtn} ${isGuest ? styles.locked : ''}`}
          ref={triggerRef}
          aria-expanded={isGuest ? undefined : open}
          aria-haspopup={isGuest ? undefined : 'menu'}
          aria-label={t('sync.history')}
          onClick={() => {
            if (isGuest) {
              requestSignIn();
              return;
            }
            setOpen((v) => !v);
          }}
        >
          <HistoryIcon />
          {isGuest && (
            <span className={styles.lockBadge} aria-hidden="true">
              <LockIcon />
            </span>
          )}
        </button>
      </span>

      {/* Статус синхронизации для скринридера: раньше был текстом в полосе,
          теперь его не видно — но по-прежнему нужно услышать. */}
      <span role="status" className="visually-hidden">
        {statusText}
      </span>

      {open && !isGuest && (
        <div className={styles.menu} role="menu" aria-label={t('sync.snapshots')}>
          {snapshots.length === 0 ? (
            <p className={styles.empty}>{t('sync.snapshot.empty')}</p>
          ) : (
            <ul className={styles.list}>
              {snapshots.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.item}
                    onClick={() => void onPick(s.id)}
                  >
                    <span className={styles.itemLabel}>{s.label || t('sync.snapshot.unnamed')}</span>
                    <span className={`${styles.itemMeta} mono`}>
                      {formatStamp(s.createdAt)} · r{s.rev}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Сообщение всплывает под кнопками отдельным слоем: в полосу, которая
          обязана оставаться одной строкой, его вставлять нельзя. */}
      <span aria-live="polite" className={message ? styles.toast : 'visually-hidden'}>
        {message}
      </span>
    </div>
  );
}
